import {
  lookupLatestCaseSchemaId,
  TDOId,
  TreeObjectId,
} from '@cbsa/state/modules/universal';
import { selectV2FoldersEnabled } from '@common/user-onboarding/selectors/organization';
import callGraphQLApi, {
  GQLApiDispatch,
  GQLApiGetState,
} from '@helpers/callGraphQLApi';
import { modules } from '@veritone/glc-redux';
import {
  createNewFolderLinkQuery,
  createNewFolderQuery,
  CreateNewFolderQueryResponse,
  createNewFolderSdoQuery,
  CreateNewFolderSdoQueryResponse,
  moveTdoQuery,
  MoveTdoQueryResponse,
  unfileTdoWithCaseQuery,
  UnfileTdoWithCaseQueryResponse,
} from '@redact-modules/casemedia/services/queries';
import {
  FolderPathItem,
  removeKeyQuotes,
  resolveFolderMutationId,
  sortFolderPath,
} from '@redact-modules/helper';
import { lookupLatestSchemaId } from '@redact-modules/mainPage/services';
import { Dispatch, ThunkAction, UnknownAction } from '@reduxjs/toolkit';
import { cloneDeep, omit } from 'lodash';
import {
  CHECK_EXISTING_TRASH_FOLDER_FAILURE,
  CHECK_EXISTING_TRASH_FOLDER_INIT,
  CHECK_EXISTING_TRASH_FOLDER_SUCCESS,
  CREATE_TRASH_FOLDER_FAILURE,
  CREATE_TRASH_FOLDER_INIT,
  CREATE_TRASH_FOLDER_SUCCESS,
  DELETE_CASE_SDO_BULK_FAILURE,
  DELETE_CASE_SDO_BULK_SUCCESS,
  DELETE_CONTENT_TEMPLATE_BULK_FAILURE,
  DELETE_CONTENT_TEMPLATE_BULK_SUCCESS,
  ENSURE_FOLDER_PARENT_PATH_FAILURE,
  ENSURE_FOLDER_PARENT_PATH_SUCCESS,
  FETCH_CASE_FILES_FAILURE,
  FETCH_CASE_FILES_SUCCESS,
  FETCH_SDOS_BULK_BY_TDO_IDS_FAILURE,
  FETCH_SDOS_BULK_BY_TDO_IDS_SUCCESS,
  FETCH_TRASH_FOLDERS_FAILURE,
  FETCH_TRASH_FOLDERS_SUCCESS,
  FETCH_TRASH_TDOS_FAILURE,
  FETCH_TRASH_TDOS_SUCCESS,
  RESTORE_SDOS_FAILURE,
  RESTORE_SDOS_SUCCESS,
} from './actions';
import { SYSTEM_FOLDER_REDACT_TRASH } from './constants';
import { FALLBACK_DOCUMENT_COMMENTS_REGISTRY_ID } from '@helpers/constants';
import {
  FolderStructuredDataObject,
  TrashCaseFile,
  TrashContentTemplate,
  TrashFolder,
  TrashTDO,
} from './models';

type Thunk<P, R = any> = (
  payload: P,
  ...args: any[]
) => ThunkAction<Promise<R | undefined>, any, unknown, UnknownAction>;

export interface CheckExistingTrashFolderQueryResponse {
  rootFolders: {
    id: string;
    name: string;
    ownerId?: string | null;
    childFolders: {
      records: {
        id: string;
        name: string;
      }[];
    };
  }[];
}

export const checkExistingTrashFolderQuery = `
query checkExistingTrashFolder {
  rootFolders(type: cms) {
    id
    name
    ownerId
    childFolders(
      names: ["${SYSTEM_FOLDER_REDACT_TRASH}"],
      limit: 1,
      nameMatch: exact,
      orderBy: {
        field: createdDateTime
        direction: asc
      }
    ) {
      records {
        id
        name
      }
    }
  }
}
`;

export const checkExistingTrashFolder =
  () => async (dispatch: Dispatch, getState: () => any) =>
    await callGraphQLApi<CheckExistingTrashFolderQueryResponse>({
      actionTypes: [
        CHECK_EXISTING_TRASH_FOLDER_INIT,
        CHECK_EXISTING_TRASH_FOLDER_SUCCESS,
        CHECK_EXISTING_TRASH_FOLDER_FAILURE,
      ],
      query: checkExistingTrashFolderQuery,
      variables: {},
      dispatch,
      getState,
    });

export interface FetchSoftDeletedTDOsResponse {
  folder: {
    childTDOs: {
      records: TrashTDO[];
    };
  };
}

// Payload of SET_TRASH_TDOS_DATA — the fetched page after the saga has stripped
// expired rows and derived the next-page flag (see handleTrashTDOsFetched).
export interface SetTrashTDOsPayload {
  folder: {
    childTDOs: {
      records: TrashTDO[];
    };
    hasNextPage: boolean;
  };
}

export const fetchSoftDeletedTDOs: Thunk<{
  trashFolderId: string;
  limit: number;
  offset: number;
  // When true, use no-op action types so the trash-page display handlers and
  // error snackbar don't fire — used by the background cleanup, which reads the
  // returned data directly instead of via the success action.
  silent?: boolean;
}> =
  ({ trashFolderId, limit, offset, silent }) =>
  async (dispatch, getState) => {
    // N+1 lookahead: fetch one extra row so the saga can tell whether a next
    // page exists without needing a (server-unavailable) exact total count.
    // `limit` stays the true page size; `fetchLimit` is the padded fetch size.
    const query = `query fetchTrashTdos ($trashFolderId: ID!, $fetchLimit: Int!, $offset: Int) {
    folder(id: $trashFolderId) {
      childTDOs (limit: $fetchLimit, offset: $offset){
        records {
          id
          name
          status
          thumbnailUrl
          createdDateTime
          modifiedDateTime
          startDateTime
          stopDateTime
          details
          primaryAsset(assetType: "media") {
            id
            signedUri
            contentType
          }
          folders {
            folderPath {
              id
              name
              parent {
                id
                name
              }
            }
          }
        }
      }
    }
  }`;

    return await callGraphQLApi<FetchSoftDeletedTDOsResponse>({
      actionTypes: silent
        ? ['noop', 'noop', 'noop']
        : ['', FETCH_TRASH_TDOS_SUCCESS, FETCH_TRASH_TDOS_FAILURE],
      query,
      variables: { trashFolderId, limit, offset, fetchLimit: limit + 1 },
      dispatch,
      getState,
      // Tolerate a `not_found` on a nested field (e.g. a TDO's primaryAsset or
      // folderPath referencing a deleted object) so one dangling reference can't
      // blank the whole trash files table — see fetchSoftDeletedFolders.
      returnPartialDataOnNotFound: true,
    });
  };

export interface FetchSoftDeletedFoldersResponse {
  folder: {
    childFolders: {
      records: TrashFolder[];
    };
  };
}

// Payload of SET_TRASH_FOLDERS_DATA — see SetTrashTDOsPayload.
export interface SetTrashFoldersPayload {
  folder: {
    childFolders: {
      records: TrashFolder[];
    };
    hasNextPage: boolean;
  };
}

export const fetchSoftDeletedFolders: Thunk<{
  trashFolderId: string;
  limit: number;
  offset: number;
  // See fetchSoftDeletedTDOs — suppresses display handlers / error snackbar for
  // the background cleanup.
  silent?: boolean;
}> =
  ({ trashFolderId, limit, offset, silent }) =>
  async (dispatch, getState) => {
    // N+1 lookahead — see fetchSoftDeletedTDOs.
    const query = `query fetchTrashFolders ($trashFolderId: ID!, $fetchLimit: Int!, $offset: Int) {
    folder(id: $trashFolderId) {
      childFolders (limit: $fetchLimit, offset: $offset){
        records {
          id
          name
          treeObjectId
          createdDateTime
          modifiedDateTime
          contentTemplates {
            id
            schemaId
            sdoId
            sdo {
              data
            }
          }
          folderPath {
            id
            name
            parent {
              id
              name
            }
          }
        }
      }
    }
  }`;

    return await callGraphQLApi<FetchSoftDeletedFoldersResponse>({
      actionTypes: silent
        ? ['noop', 'noop', 'noop']
        : ['', FETCH_TRASH_FOLDERS_SUCCESS, FETCH_TRASH_FOLDERS_FAILURE],
      query,
      variables: { trashFolderId, limit, offset, fetchLimit: limit + 1 },
      dispatch,
      getState,
      // A trashed folder can carry a content template whose SDO was deleted out
      // from under it (e.g. a partially-completed permanent delete/restore). The
      // platform then returns `not_found` for that nested `sdo` alongside the
      // valid folder records. Tolerate that so the rest of the trash table still
      // renders instead of blanking on a single dangling reference.
      returnPartialDataOnNotFound: true,
    });
  };

// Where a trashed folder lives right now. A restore / permanent delete driven by
// the selection snapshot (VE-29191) re-reads each folder first: the snapshot
// can be stale (restored meanwhile in another tab, or by another user).
// "gone" needs positive evidence — the folder no longer exists, or it has
// another parent; anything else is "unconfirmed" and the folder is left alone.
export type TrashFolderState =
  | {
      readonly status: 'inTrash';
      readonly contentTemplates: ReadonlyArray<TrashContentTemplate>;
    }
  | { readonly status: 'gone' }
  | { readonly status: 'unconfirmed' };

export const fetchTrashFolderState =
  ({ folderId, trashFolderId }: { folderId: string; trashFolderId: string }) =>
  async (dispatch: Dispatch, getState: GQLApiGetState) => {
    let data:
      | {
          folder?: {
            parent?: { id?: string | null } | null;
            contentTemplates?: ReadonlyArray<TrashContentTemplate> | null;
          } | null;
        }
      | undefined;
    try {
      data = await callGraphQLApi({
        actionTypes: ['noop', 'noop', 'noop'],
        query: `query trashFolderState($folderId: ID!) {
          folder(id: $folderId) {
            id
            parent {
              id
            }
            contentTemplates {
              id
              schemaId
              sdoId
              sdo {
                data
              }
            }
          }
        }`,
        variables: { folderId },
        dispatch,
        getState,
        // A dangling nested trash SDO (VE-25907) must not hide the folder.
        returnPartialDataOnNotFound: true,
      });
    } catch {
      return { status: 'unconfirmed' } as TrashFolderState;
    }
    if (!data) {
      return { status: 'unconfirmed' } as TrashFolderState;
    }
    if (!data.folder) {
      return { status: 'gone' } as TrashFolderState;
    }
    const parentId = data.folder.parent?.id;
    if (!parentId) {
      return { status: 'unconfirmed' } as TrashFolderState;
    }
    return (
      parentId === trashFolderId
        ? {
            status: 'inTrash',
            contentTemplates: data.folder.contentTemplates ?? [],
          }
        : { status: 'gone' }
    ) as TrashFolderState;
  };

export interface FetchCaseChildTDOsResponse {
  folder: {
    childTDOs: {
      records: TrashCaseFile[];
    };
  };
}

export const fetchCaseChildFiles: Thunk<{
  treeObjectId: TreeObjectId;
  limit: number;
  offset: number;
}> =
  ({ treeObjectId, limit, offset }) =>
  async (dispatch, getState) => {
    // N+1 lookahead — see fetchSoftDeletedTDOs. The reducer slices the extra
    // row off and turns it into the `hasNextPage` flag.
    const query = `query fetchTrashFolderCaseFiles($treeObjectId: ID!, $fetchLimit: Int!, $offset: Int) {
      folder(id: $treeObjectId) {
        childTDOs(limit: $fetchLimit, offset: $offset) {
          records {
            id
            name
            createdDateTime
            modifiedDateTime
            startDateTime
            stopDateTime
            details
            primaryAsset(assetType: "media") {
              id
              signedUri
              contentType
            }
          }
        }
      }
    }`;

    return await callGraphQLApi<FetchCaseChildTDOsResponse>({
      actionTypes: ['', FETCH_CASE_FILES_SUCCESS, FETCH_CASE_FILES_FAILURE],
      query,
      variables: { treeObjectId, limit, offset, fetchLimit: limit + 1 },
      dispatch,
      getState,
    });
  };

const getFolderChildByNameQuery = `
query getFolderChildByName($parentId: ID!, $name: String!) {
  folder(id: $parentId) {
    childFolders(names: [$name], limit: 1000, nameMatch: exact) {
      records {
        id
        parent {
          id
        }
        contentTemplates {
          id
        }
      }
    }
  }
}
`;

interface GetFolderChildByNameResponse {
  folder: {
    childFolders: {
      records: {
        id: string;
        parent: { id: string };
        contentTemplates?: { id: string }[] | null;
      }[];
    };
  } | null;
}

/**
 * A folder resolved as a direct child of an expected parent, by exact name.
 * `isCase` distinguishes a request/case from a plain folder: in this app a
 * request carries a content template and a plain folder carries none. This is
 * the same signal the folder view uses to render requests vs folders (see
 * casemedia `persistLastOpenFolderFromState`).
 */
interface DirectChild {
  id: string;
  isCase: boolean;
}

/**
 * Find the folder with `name` that is a DIRECT child of `parentId`, if any.
 * The name filter can return matches at any depth, so we keep only records
 * whose parent is the expected one. Folder names are not unique under a parent,
 * so a plain folder and a request can legitimately coexist with the same name
 * (the premise of VE-25859) — when they do, prefer the request/case so restore
 * reuses it instead of skipping to a "(restore)" duplicate.
 * Returns undefined on no match or error.
 */
const findDirectChild = async (
  parentId: string,
  name: string,
  dispatch: Dispatch,
  getState: () => any
): Promise<DirectChild | undefined> => {
  try {
    const result: GetFolderChildByNameResponse | undefined =
      await callGraphQLApi<GetFolderChildByNameResponse>({
        actionTypes: ['noop', 'noop', 'noop'],
        query: getFolderChildByNameQuery,
        variables: { parentId, name },
        dispatch,
        getState,
      });
    const directChildren = (result?.folder?.childFolders?.records ?? []).filter(
      (r) => r.parent.id === parentId
    );
    const record =
      directChildren.find((r) => !!r.contentTemplates?.length) ??
      directChildren[0];
    if (!record) {
      return undefined;
    }
    return { id: record.id, isCase: !!record.contentTemplates?.length };
  } catch {
    return undefined;
  }
};

/** Create a plain (non-case) folder under `parentId`; returns its id. */
const createPlainFolder = async (
  parentId: string,
  name: string,
  dispatch: Dispatch,
  getState: () => any
): Promise<string | undefined> => {
  const createResult: CreateNewFolderQueryResponse | undefined =
    await callGraphQLApi<CreateNewFolderQueryResponse>({
      actionTypes: ['noop', 'noop', 'noop'],
      query: createNewFolderQuery,
      variables: { parentId, newFolderName: name },
      dispatch,
      getState,
      throwOnError: true,
    });
  return createResult?.createFolder?.id;
};

/**
 * Create a request/case under `parentId`: the folder object, its case SDO, and
 * the link between them. Returns the new folder id. The schema is looked up
 * before the folder is created so a failed lookup creates nothing. Note the
 * remaining steps are not transactional: if the SDO or link step fails after
 * the folder is created, the folder is left behind (as a plain, content-
 * template-less folder) and the failure is surfaced to the caller as a throw.
 */
const createCaseFolder = async (
  parentId: string,
  name: string,
  dispatch: Dispatch,
  getState: () => any
): Promise<string | undefined> => {
  const schemaId = await lookupLatestCaseSchemaId(dispatch, getState);
  if (!schemaId) {
    return undefined;
  }

  const createResult: CreateNewFolderQueryResponse | undefined =
    await callGraphQLApi<CreateNewFolderQueryResponse>({
      actionTypes: ['noop', 'noop', 'noop'],
      query: createNewFolderQuery,
      variables: { parentId, newFolderName: name },
      dispatch,
      getState,
      throwOnError: true,
    });
  const newId = createResult?.createFolder?.id;
  const newTreeObjectId = createResult?.createFolder?.treeObjectId;
  if (!newId || !newTreeObjectId) {
    return undefined;
  }

  const currentTime = new Date().toISOString();
  const sdoResult: CreateNewFolderSdoQueryResponse | undefined =
    await callGraphQLApi<CreateNewFolderSdoQueryResponse>({
      actionTypes: ['noop', 'noop', 'noop'],
      query: createNewFolderSdoQuery,
      variables: {
        newFolderName: name,
        folderTreeObjectId: newTreeObjectId,
        currentTime,
        schemaId,
      },
      dispatch,
      getState,
      throwOnError: true,
    });
  const newSdoId = sdoResult?.createStructuredData?.id;
  if (!newSdoId) {
    return undefined;
  }

  // VE-21114: v2 authorizes createFolderContentTemplate via OLP against the
  // folder id; the deprecated treeObjectId returns not_allowed. Reads still
  // accept either, so swap the id under v2 only.
  const isV2Folders = selectV2FoldersEnabled(getState());
  await callGraphQLApi({
    actionTypes: ['noop', 'noop', 'noop'],
    query: createNewFolderLinkQuery,
    variables: {
      schemaId,
      folderId: resolveFolderMutationId(isV2Folders, {
        folderId: newId,
        treeObjectId: newTreeObjectId,
      }),
      sdoId: newSdoId,
    },
    dispatch,
    getState,
    throwOnError: true,
  });

  return newId;
};

// Upper bound on the "(restore) (n)" search so a pathological set of existing
// names can never spin an unbounded query loop; far beyond any realistic case.
const MAX_RESTORE_NAME_ATTEMPTS = 100;

/**
 * Resolve the request/case that a restored file should land in, under
 * `parentId`, for a file whose original request was named `baseName`.
 *
 * A plain folder sharing the name is never used as the destination: files
 * inside a plain folder are not rendered by the folder view, so the file would
 * silently disappear (VE-25859). Precedence:
 *   1. reuse a same-named request (normal case, or the request restored earlier);
 *   2. reuse an existing "<name> (restore)" request (keeps multiple files from
 *      the same deleted request together);
 *   3. create a new request under the first available name in the sequence
 *      "<name>", "<name> (restore)", "<name> (restore) (2)", ...
 *
 * `sameName` is the already-fetched direct child for `baseName`, passed in to
 * avoid a duplicate lookup.
 */
const resolveRestoreCaseDestination = async (
  parentId: string,
  baseName: string,
  sameName: DirectChild | undefined,
  dispatch: Dispatch,
  getState: () => any
): Promise<string | undefined> => {
  if (sameName?.isCase) {
    return sameName.id;
  }

  const restoreName = `${baseName} (restore)`;
  const restore = await findDirectChild(
    parentId,
    restoreName,
    dispatch,
    getState
  );
  if (restore?.isCase) {
    return restore.id;
  }

  // No reusable request exists — create one under the first free name.
  if (!sameName) {
    return createCaseFolder(parentId, baseName, dispatch, getState);
  }
  if (!restore) {
    return createCaseFolder(parentId, restoreName, dispatch, getState);
  }
  for (let attempt = 2; attempt <= MAX_RESTORE_NAME_ATTEMPTS; attempt++) {
    const candidate = `${restoreName} (${attempt})`;
    const existing = await findDirectChild(
      parentId,
      candidate,
      dispatch,
      getState
    );
    if (!existing) {
      return createCaseFolder(parentId, candidate, dispatch, getState);
    }
  }
  throw new Error(
    `Unable to find an available restore name for "${baseName}" after ${MAX_RESTORE_NAME_ATTEMPTS} attempts`
  );
};

/**
 * Maps `${parentId}::${name}` to a resolved folder id within a single restore
 * run. Passed across the per-file loop in `restoreSoftDeletedTdos` so that
 * folders/requests created for the first file are reused by later files with
 * the same path, instead of being re-looked-up against an index that may lag
 * behind the just-issued write (there is no read-your-writes guarantee, and
 * folder names are not unique — see VE-25859). Without this, several files
 * restored from the same deleted request could each mint their own
 * "<name> (restore) (n)" and scatter.
 */
type FolderResolutionCache = Map<string, string>;

/**
 * Walks `oldFolderPath` from root to leaf, verifying each folder exists as a
 * direct child of its expected parent (by name). If a folder is missing from
 * its expected parent (deleted or moved away), it is recreated there along
 * with all subsequent folders in the path.
 *
 * When `lastItemIsCase` is true, the final path item is resolved to a
 * request/case via `resolveRestoreCaseDestination` — reusing an existing
 * request or creating a new (possibly "(restore)"-suffixed) one, but never
 * targeting a plain folder that merely shares the name.
 *
 * `cache` (optional) dedupes resolution across calls in the same restore batch;
 * see `FolderResolutionCache`.
 *
 * Returns the ID of the final destination folder.
 */
export const ensureFolderPathExists = async (
  oldFolderPath: FolderPathItem[],
  dispatch: Dispatch,
  getState: () => any,
  lastItemIsCase = false,
  cache?: FolderResolutionCache
): Promise<string | undefined> => {
  const sorted = sortFolderPath(oldFolderPath);
  if (sorted.length === 0) {
    return undefined;
  }

  let currentParentId: string | undefined = undefined;

  for (const [index, item] of sorted.entries()) {
    const isLast = index + 1 === sorted.length;

    if (item.parent === null) {
      // CMS root folder — always exists; use its ID as the base parent
      currentParentId = item.id;
      continue;
    }

    if (!currentParentId) {
      return undefined;
    }

    // `parentId` is `string` here (guarded above). The explicit annotation is
    // load-bearing: it breaks a TS7022 circular-inference cycle that would
    // otherwise route cacheKey -> currentParentId (reassigned from cached) ->
    // cacheKey.
    const parentId: string = currentParentId;
    const cacheKey = `${parentId}::${item.name}`;
    const cached = cache?.get(cacheKey);
    if (cached) {
      currentParentId = cached;
      continue;
    }

    if (isLast && lastItemIsCase) {
      // The final path item is the file's original request; it must resolve to
      // a request/case, never a plain folder that happens to share the name.
      const existingChild = await findDirectChild(
        parentId,
        item.name,
        dispatch,
        getState
      );
      const resolved = await resolveRestoreCaseDestination(
        parentId,
        item.name,
        existingChild,
        dispatch,
        getState
      );
      if (!resolved) {
        return undefined;
      }
      cache?.set(cacheKey, resolved);
      currentParentId = resolved;
      continue;
    }

    const existingChild = await findDirectChild(
      parentId,
      item.name,
      dispatch,
      getState
    );
    if (existingChild) {
      currentParentId = existingChild.id;
    } else {
      // Folder doesn't exist under expected parent — recreate it as a plain folder
      currentParentId = await createPlainFolder(
        parentId,
        item.name,
        dispatch,
        getState
      );
      if (!currentParentId) {
        return undefined;
      }
    }
    cache?.set(cacheKey, currentParentId);
  }

  return currentParentId;
};

/**
 * Thunk that ensures the parent folder path of a trashed folder still exists,
 recreating any missing intermediate folders by name.
 */
export const ensureFolderParentPath =
  ({
    oldFolderPath,
    folderId,
  }: {
    oldFolderPath: FolderPathItem[];
    folderId: TreeObjectId;
  }) =>
  async (dispatch: Dispatch, getState: () => any): Promise<void> => {
    try {
      // oldFolderPath for a folder includes the folder itself as the last item.
      const parentPath = oldFolderPath.slice(0, -1);
      const parentId = await ensureFolderPathExists(
        parentPath,
        dispatch,
        getState
      );
      if (!parentId) {
        dispatch(ENSURE_FOLDER_PARENT_PATH_FAILURE({ folderId }));
        return;
      }
      dispatch(ENSURE_FOLDER_PARENT_PATH_SUCCESS({ folderId, parentId }));
    } catch {
      dispatch(ENSURE_FOLDER_PARENT_PATH_FAILURE({ folderId }));
    }
  };

const buildTdoDetails = (tdo: TrashTDO) => {
  const updatedTags = cloneDeep([...(tdo?.details?.tags || [])]);
  if (updatedTags?.[0]) {
    // @ts-expect-error TODO: Why is it valid to just put this on the first tag?
    updatedTags[0] = omit(updatedTags[0], [
      'oldFolderPath',
      'trashRetentionDays',
      // Legacy stamp on items trashed before the trashRetentionDays rename.
      'numberOfDaysToKeep',
      'movedToTrashDateTime',
      'toBeDeletedTime',
      'movedToTrashByName',
      'movedToTrashById',
    ]);
  }
  return removeKeyQuotes(
    JSON.stringify({
      ...(tdo?.details || {}),
      tags: updatedTags,
    })
  );
};

export interface RestoreTdosResult {
  // Moved back and stripped of their trash tags.
  readonly restoredTdoIds: TDOId[];
  // Moved back, but the tag strip failed: out of Trash yet still tagged as
  // trashed, so Home hides them. Reported separately, never as restored.
  readonly leftoverTdoIds: TDOId[];
  // Not moved: still in Trash.
  readonly failedTdoIds: TDOId[];
}

/**
 * Restores `tdos` one file at a time: move it back, then strip its trash tags.
 * Doing both per file (not all moves, then one tag batch) keeps "restored"
 * true for every id reported as such, and lets the saga drop exactly the files
 * that left Trash from the selection — so a retry after a partial failure
 * doesn't stall on a file that is no longer there. One failing file doesn't stop
 * the others. Never throws.
 */
export const restoreSoftDeletedTdos =
  ({
    tdos,
    trashFolderId,
  }: {
    readonly tdos: readonly TrashTDO[];
    readonly trashFolderId: string;
  }) =>
  async (dispatch: Dispatch, getState: GQLApiGetState) => {
    const result: RestoreTdosResult = {
      restoredTdoIds: [],
      leftoverTdoIds: [],
      failedTdoIds: [],
    };
    // Shared across all files in this restore so multiple files from the same
    // deleted request land in one recreated request — see FolderResolutionCache.
    const folderCache: FolderResolutionCache = new Map();

    for (const tdo of tdos) {
      try {
        const tag = tdo.details.tags?.[0];
        const oldFolderPath =
          typeof tag === 'object' && tag !== null && 'oldFolderPath' in tag
            ? tag.oldFolderPath
            : undefined;
        const destinationFolderId =
          oldFolderPath && oldFolderPath.length > 0
            ? await ensureFolderPathExists(
                oldFolderPath,
                dispatch,
                getState,
                true, // last item is always a case/request for TDOs
                folderCache
              )
            : undefined;

        await callGraphQLApi<
          MoveTdoQueryResponse | UnfileTdoWithCaseQueryResponse
        >({
          actionTypes: [
            'trash/move-or-unfile-tdo-init',
            'trash/move-or-unfile-tdo-success',
            'trash/move-or-unfile-tdo-failure',
          ],
          query: destinationFolderId ? moveTdoQuery : unfileTdoWithCaseQuery,
          variables: {
            tdoId: tdo.id,
            oldFolderId: trashFolderId,
            newFolderId: destinationFolderId,
          },
          dispatch,
          getState,
          throwOnError: true,
        });
      } catch {
        result.failedTdoIds.push(tdo.id);
        continue;
      }

      try {
        await callGraphQLApi<{ i0: { id: string } }>({
          actionTypes: [
            'trash/restore-trash-tdos-init',
            '',
            'trash/restore-trash-tdos-details-failure',
          ],
          query: `mutation restoreSoftDeletedTDOs {
            i0:updateTDO(input: {id:${tdo.id}, details: ${buildTdoDetails(tdo)}})
              {
                id
              }
          }`,
          variables: {},
          dispatch,
          getState,
          throwOnError: true,
        });
        result.restoredTdoIds.push(tdo.id);
      } catch {
        result.leftoverTdoIds.push(tdo.id);
      }
    }

    return result;
  };

const buildSdoData = (sdo: FolderStructuredDataObject) => {
  let updatedSdoData = cloneDeep(sdo.data || {});
  if (updatedSdoData) {
    updatedSdoData = omit(updatedSdoData, [
      'oldFolderPath',
      'trashRetentionDays',
      // Legacy stamp on items trashed before the trashRetentionDays rename.
      'numberOfDaysToKeep',
      'movedToTrashDateTime',
      'movedToTrashByName',
      'movedToTrashById',
      'isCase',
    ]) as typeof sdo.data;
  }
  return removeKeyQuotes(JSON.stringify(updatedSdoData));
};

export type RestoreSdosResponse = Record<
  string,
  {
    id: string;
  }
>;

export const restoreSoftDeletedSdos: Thunk<
  {
    readonly sdos: FolderStructuredDataObject[];
  },
  RestoreSdosResponse
> =
  ({ sdos }) =>
  async (dispatch, getState) => {
    const schemaId = await lookupLatestCaseSchemaId(dispatch, getState);
    if (!schemaId) {
      return;
    }
    const query = `mutation restoreSoftDeletedSDOs {
     ${sdos
       .map(
         (sdo, index) => `
            i${index}:createStructuredData(
              input: {
                id: "${sdo.id}",
                schemaId: "${schemaId}",
                data: ${buildSdoData(sdo)}
              }) {
              id
            }`
       )
       .join(',')}
    }`;

    return await callGraphQLApi<RestoreSdosResponse>({
      actionTypes: ['', RESTORE_SDOS_SUCCESS, RESTORE_SDOS_FAILURE],
      query,
      variables: {},
      dispatch,
      getState,
    });
  };

export type DeleteCaseSDOBulkResponse = Record<
  string,
  {
    id: string;
  }
>;
export const deleteCaseSDOBulk: Thunk<
  {
    readonly sdoIds: string[];
  },
  DeleteCaseSDOBulkResponse
> =
  ({ sdoIds }) =>
  async (dispatch, getState) => {
    const schemaId = await lookupLatestCaseSchemaId(dispatch, getState);
    if (!schemaId) {
      return;
    }
    const query = `mutation deleteSDOs {
      ${sdoIds
        .map(
          (sdoId, index) => `
          i${index}: deleteStructuredData(
            input: {
              id: "${sdoId}",
              schemaId: "${schemaId}"
            }) {
            id
          }`
        )
        .join(',')}
    }`;

    return await callGraphQLApi<DeleteCaseSDOBulkResponse>({
      actionTypes: [
        '',
        DELETE_CASE_SDO_BULK_SUCCESS,
        DELETE_CASE_SDO_BULK_FAILURE,
      ],
      query,
      variables: {},
      dispatch,
      getState,
      // Best-effort tolerance only: if the server ever answers with partial data
      // (some aliases deleted, others `not_found`), take it rather than reading
      // the whole batch as a failure.
      //
      // Do NOT rely on this to survive an already-deleted SDO. Verified against a
      // live org: on this mutation a `not_found` collapses `data` to null, and
      // callGraphQLApi's partial-data branch requires `response.data` to be
      // truthy — so it falls through to failure and still returns undefined.
      // Callers must avoid *asking* to delete an SDO that's already gone; that's
      // what the `sdo`-present filters in the trash sagas do (VE-25907).
      returnPartialDataOnNotFound: true,
    });
  };

/**
 * Delete multiple folder content templates by ID
 */
export type DeleteContentTemplateBulkResponse = Record<
  string,
  {
    id: string;
  }
>;
export const deleteContentTemplateBulk: Thunk<
  {
    readonly contentTemplateIds: string[];
  },
  DeleteContentTemplateBulkResponse
> =
  ({ contentTemplateIds }) =>
  async (dispatch, getState) => {
    const query = `mutation deleteFolderContentTemplates {
      ${contentTemplateIds
        .map(
          (id, index) => `i${index}: deleteFolderContentTemplate(
            id: "${id}"
          ) {
            id
          }`
        )
        .join(',')}
    }`;

    return await callGraphQLApi<DeleteContentTemplateBulkResponse>({
      actionTypes: [
        '',
        DELETE_CONTENT_TEMPLATE_BULK_SUCCESS,
        DELETE_CONTENT_TEMPLATE_BULK_FAILURE,
      ],
      query,
      variables: {},
      dispatch,
      getState,
      // Best-effort partial-data tolerance, with the same caveat as
      // deleteCaseSDOBulk above: it does not rescue an already-unlinked template,
      // because a `not_found` nulls `data` entirely. Kept for the mixed-batch case.
      returnPartialDataOnNotFound: true,
    });
  };

export const createTrashFolder =
  ({
    newFolderName,
    rootFolderId,
  }: {
    newFolderName: string;
    rootFolderId: string;
  }) =>
  async (dispatch: Dispatch, getState: () => any) =>
    await callGraphQLApi<CreateNewFolderQueryResponse>({
      actionTypes: [
        CREATE_TRASH_FOLDER_INIT.type,
        CREATE_TRASH_FOLDER_SUCCESS.type,
        CREATE_TRASH_FOLDER_FAILURE.type,
      ],
      query: createNewFolderQuery,
      variables: {
        parentId: rootFolderId,
        newFolderName,
      },
      dispatch,
      getState,
      throwOnError: true,
    });

export type FetchSDOsBulkByTDOIdsResponse = Record<
  string,
  {
    records: {
      id: string;
    }[];
  }
>;
export const fetchSDOsBulkByTDOIds: Thunk<
  {
    readonly tdoIds: TDOId[];
    /** Skip the shared terminal actions; the caller reads the return value. */
    readonly silent?: boolean;
  },
  FetchSDOsBulkByTDOIdsResponse
> =
  ({ tdoIds, silent }) =>
  async (dispatch, getState) => {
    const schemaId = await lookupLatestSchemaId(dispatch, getState);
    if (!schemaId || tdoIds.length === 0) {
      return {};
    }

    const query = `query bulkFetchSDOs {
      ${tdoIds
        .map(
          (tdoId) => `tdo_${tdoId}: structuredDataObjects(
            schemaId: "${schemaId}",
            owned: true,
            filter: {
              tdoId: "${tdoId}"
            }
          ) {
            records {
              id
            }
          }`
        )
        .join('\n')}
    }`;

    return await callGraphQLApi<FetchSDOsBulkByTDOIdsResponse>({
      actionTypes: silent
        ? ['noop', 'noop', 'noop']
        : [
            'noop',
            FETCH_SDOS_BULK_BY_TDO_IDS_SUCCESS,
            FETCH_SDOS_BULK_BY_TDO_IDS_FAILURE,
          ],
      query,
      variables: {},
      dispatch,
      getState,
    });
  };

// ---------------------------------------------------------------------------
// Comment SDOs left behind by a permanent delete (VE-28551)
//
// Media comments and document comments are per-TDO rows in their own
// registries, and `deleteTDO` cascades to neither — they are keyed by tdoId
// rather than parented to the TDO. Stranded, they keep the comment text and
// the author's identity for a file that no longer exists, which is a retention
// problem rather than clutter.
//
// Deliberately NOT routed through the modules' own `lookupLatest*SchemaId`
// helpers: those dispatch SCHEMA_ID_NOT_FOUND, which a global watcher turns
// into a red snackbar. This runs after a delete the user has already been told
// succeeded, so an org that never provisioned comments must go quiet, not
// accuse the user's successful deletion of a configuration error.
// ---------------------------------------------------------------------------

/**
 * The document-comments registry has no config key in any environment; the
 * module runs on this fallback (see `cbsa-app/state/modules/universal/services`,
 * VE-21982). Purging has to look in the same place the writer wrote.
 */
const DOCUMENT_COMMENTS_FALLBACK_REGISTRY_ID =
  FALLBACK_DOCUMENT_COMMENTS_REGISTRY_ID;

/** Rows read per request, and rows deleted per mutation document. */
const COMMENT_PAGE_SIZE = 100;
const COMMENT_DELETE_CHUNK = 25;

/** Bounds the paging loop; 200 pages is far past any real comment count. */
const COMMENT_PAGE_LIMIT = 200;

/** A server-assigned SDO id — the only shape allowed into a mutation document. */
const COMMENT_SDO_ID_PATTERN = /^[0-9a-f-]{36}$/i;

const NOOP_ACTION = 'redact/trash/NOOP';

const { config: configModule } = modules;

/** Registry → published schema id, without the not-found snackbar. */
const resolveSchemaIdQuietly = async (
  dataRegistryId: string,
  dispatch: GQLApiDispatch,
  getState: GQLApiGetState
): Promise<string | undefined> => {
  const response = await callGraphQLApi<{
    dataRegistry?: { publishedSchema?: { id?: string } | null } | null;
  }>({
    actionTypes: [NOOP_ACTION, NOOP_ACTION, NOOP_ACTION],
    query: `
      query resolveCommentSchemaId($dataRegistryId: ID!) {
        dataRegistry(id: $dataRegistryId) {
          id
          publishedSchema {
            id
          }
        }
      }
    `,
    variables: { dataRegistryId },
    dispatch,
    getState,
  });
  return response?.dataRegistry?.publishedSchema?.id ?? undefined;
};

/**
 * Comment ids for a BATCH of TDOs, one aliased query per round.
 *
 * Per-TDO round trips were the first shape here, and they do not survive the
 * case this exists for: a 500-file request delete meant ~1000 sequential
 * queries in a promise nobody holds, after the user has moved on — closing the
 * tab took the rest with it. The aliased shape is the one
 * `fetchSDOsBulkByTDOIds` already uses a few hundred lines above.
 *
 * Returns ids grouped by TDO so one bad file can be dropped without taking the
 * rest of the batch with it (see purgeCommentsForTdos).
 */
const collectCommentIdsByTdo = async (
  tdoIds: readonly string[],
  schemaId: string,
  dispatch: GQLApiDispatch,
  getState: GQLApiGetState
): Promise<Map<string, string[]>> => {
  const byTdo = new Map<string, string[]>(tdoIds.map((tdoId) => [tdoId, []]));
  let outstanding = [...tdoIds];

  // Rounds, not offsets: each round asks for the first page of whatever is
  // still there. A TDO returning a full page has more and is asked again.
  for (
    let round = 0;
    round < COMMENT_PAGE_LIMIT && outstanding.length > 0;
    round += 1
  ) {
    const query = `query fetchCommentIds($schemaId: ID!, $limit: Int!) {
      ${outstanding
        .map(
          (tdoId, index) => `
          t${index}: structuredDataObjects(
            schemaId: $schemaId
            filter: { tdoId: "${tdoId}" }
            limit: $limit
            offset: ${byTdo.get(tdoId)?.length ?? 0}
          ) {
            records {
              id
            }
          }`
        )
        .join('\n')}
    }`;

    const response = await callGraphQLApi<
      Record<string, { records?: { id: string }[] } | undefined>
    >({
      actionTypes: [NOOP_ACTION, NOOP_ACTION, NOOP_ACTION],
      query,
      variables: { schemaId, limit: COMMENT_PAGE_SIZE },
      dispatch,
      getState,
    });
    if (!response) {
      break;
    }

    const stillOutstanding: string[] = [];
    outstanding.forEach((tdoId, index) => {
      const records = response[`t${index}`]?.records ?? [];
      // Ids are interpolated into the delete mutation (the alias count varies
      // per call), so anything that is not a plain uuid is dropped rather than
      // trusted.
      const ids = records
        .map((record) => record.id)
        .filter((id) => COMMENT_SDO_ID_PATTERN.test(id));
      byTdo.get(tdoId)?.push(...ids);
      if (records.length === COMMENT_PAGE_SIZE) {
        stillOutstanding.push(tdoId);
      }
    });
    outstanding = stillOutstanding;
  }
  return byTdo;
};

const deleteCommentIds = async (
  ids: string[],
  schemaId: string,
  dispatch: GQLApiDispatch,
  getState: GQLApiGetState
): Promise<void> => {
  for (let i = 0; i < ids.length; i += COMMENT_DELETE_CHUNK) {
    const chunk = ids.slice(i, i + COMMENT_DELETE_CHUNK);
    await callGraphQLApi<Record<string, unknown>>({
      actionTypes: [NOOP_ACTION, NOOP_ACTION, NOOP_ACTION],
      query: `mutation deleteCommentSdos {
        ${chunk
          .map(
            (id, index) => `
            c${index}: deleteStructuredData(
              input: { id: "${id}", schemaId: "${schemaId}" }
            ) {
              id
            }`
          )
          .join(',')}
      }`,
      variables: {},
      dispatch,
      getState,
    });
  }
};

/**
 * Purge every media and document comment belonging to these TDOs.
 *
 * Best-effort throughout, and per registry: a media-comments registry that
 * cannot be resolved must not stop the document comments being cleaned up.
 * Never throws — the file is already gone, and a failure here is a row to
 * collect on another pass, not a failed deletion to report.
 */
export const purgeCommentsForTdos =
  (tdoIds: readonly string[]) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState): Promise<void> => {
    if (tdoIds.length === 0) {
      return;
    }
    const {
      redactMediaCommentRegistryId,
      redactDocumentCommentsRegistryId = DOCUMENT_COMMENTS_FALLBACK_REGISTRY_ID,
    } = configModule.getConfig<Window['config']>(getState() as never);

    const registries: { name: string; id?: string }[] = [
      {
        name: 'redactMediaCommentRegistryId',
        id: redactMediaCommentRegistryId,
      },
      {
        name: 'redactDocumentCommentsRegistryId',
        id: redactDocumentCommentsRegistryId,
      },
    ];

    for (const registry of registries) {
      if (!registry.id) {
        continue;
      }
      let schemaId: string | undefined;
      try {
        schemaId = await resolveSchemaIdQuietly(
          registry.id,
          dispatch,
          getState
        );
      } catch {
        // Fixed vocabulary only — never comment text or a server message.
        console.error('trash: comment registry unresolved; comments retained', {
          registry: registry.name,
          tdoCount: tdoIds.length,
        });
        continue;
      }
      if (!schemaId) {
        continue;
      }

      let byTdo: Map<string, string[]>;
      try {
        byTdo = await collectCommentIdsByTdo(
          tdoIds,
          schemaId,
          dispatch,
          getState
        );
      } catch {
        console.error('trash: comment lookup failed; comments retained', {
          registry: registry.name,
          tdoCount: tdoIds.length,
        });
        continue;
      }

      // Per TDO, so one file's failure costs that file rather than every file
      // after it. There is no second pass: these TDOs are deleted, so nothing
      // will ever enumerate them again — whatever is skipped here is stranded
      // for good.
      for (const [tdoId, ids] of byTdo) {
        if (ids.length === 0) {
          continue;
        }
        try {
          await deleteCommentIds(ids, schemaId, dispatch, getState);
        } catch {
          console.error('trash: comment delete failed; comments retained', {
            registry: registry.name,
            tdoId,
            rowCount: ids.length,
          });
        }
      }
    }
  };
